/** Original product narratives. Capability sources: docs/marketing-evidence.md. */
export type ProductStory = {
  action: { label: string; href: string };
  promise: string;
  detail: string;
  workload: { label: string; title: string; body: string; outcome: string };
  layers: [string, string, string];
  facts: [string, string][];
  responsibilities: [string, string][];
};
export const productStories: Record<string, ProductStory> = {
  "virtual-machines": {
    action: {
      label: "Explore VM configurations",
      href: "/app/marketplace/category/compute",
    },
    promise: "Your application. Your operating system.",
    detail:
      "Run a Linux guest on QEMU/KVM. Install your application packages and administer the operating system through SSH or its browser console.",
    workload: {
      label: "Application architecture / Virtual machines",
      title: "Give a custom stack a place to run.",
      body: "Use a VM for software that needs system packages, scheduled jobs or a custom Linux setup. Keep package versions and initialization instructions with your project. Connect a separate database through the network paths you configure.",
      outcome:
        "A guest environment you can document, reproduce and maintain through your own release process.",
    },
    layers: [
      "Your application & system packages",
      "Linux guest · SSH · cloud-init",
      "QEMU / KVM virtual machine",
    ],
    facts: [
      ["Deployment input", "Linux image + initial configuration"],
      ["Administration", "Guest operating system access"],
      ["Best fit", "Custom stacks and self-managed databases"],
    ],
    responsibilities: [
      [
        "Platform tools",
        "Provisioning, power actions, browser console and supported snapshot operations.",
      ],
      [
        "Your team",
        "Guest patches, application security, data backups and recovery testing.",
      ],
      [
        "Offer details",
        "CPU, memory, storage, images and available actions come from the selected configuration.",
      ],
    ],
  },
  containers: {
    action: {
      label: "Explore container configurations",
      href: "/app/marketplace/category/containers",
    },
    promise: "Bring the image. Keep your release process.",
    detail:
      "Deploy services from compatible OCI images on Podman. Configure environment variables and ports, follow lifecycle actions and inspect service logs.",
    workload: {
      label: "Application architecture / Containers",
      title: "Make the release artifact the starting point.",
      body: "Package each API or worker in a versioned image. Supply its environment, ports and dependency connections at deployment. Review storage options before persisting application data.",
      outcome:
        "A clear relationship between the image you build and the service your team operates.",
    },
    layers: [
      "Versioned OCI image",
      "Environment · ports · dependencies",
      "Podman application runtime",
    ],
    facts: [
      ["Deployment input", "Compatible OCI image"],
      ["Administration", "Image, configuration and service logs"],
      ["Best fit", "Application APIs and background workers"],
    ],
    responsibilities: [
      [
        "Platform tools",
        "Container deployment, service lifecycle actions and available log views.",
      ],
      [
        "Your team",
        "Image updates, application dependencies, configuration and persistent data.",
      ],
      [
        "Offer details",
        "Check image access, architecture, resources and storage options for the selected service.",
      ],
    ],
  },
  functions: {
    action: {
      label: "Explore function offers",
      href: "/marketplace/search?family=function",
    },
    promise: "Give one HTTP task its own deployment.",
    detail:
      "Deploy focused application logic behind an HTTP endpoint. Keep webhooks, validation and integrations separate from your main application.",
    workload: {
      label: "Application architecture / Functions",
      title: "Turn an incoming request into a useful result.",
      body: "Validate the incoming request, run the handler and return a response. Handle caller retries in your code and keep durable state in an appropriate external service.",
      outcome:
        "A focused endpoint with an explicit input, response and failure path.",
    },
    layers: [
      "HTTP request",
      "Your handler · configuration · dependencies",
      "Response to the caller",
    ],
    facts: [
      ["Deployment input", "Function source and configuration"],
      ["Execution model", "HTTP invocation"],
      ["Best fit", "Webhooks, validation and integrations"],
    ],
    responsibilities: [
      [
        "Platform tools",
        "Function deployment, invocation endpoint and available execution logs.",
      ],
      [
        "Your team",
        "Handler logic, dependencies, request authentication and external data storage.",
      ],
      [
        "Offer details",
        "Execution limits, resources and billing units are offer-specific. Automatic scaling and scale-to-zero are not implied.",
      ],
    ],
  },
  networking: {
    action: { label: "Discuss your network design", href: "/contact" },
    promise: "Connect the services that belong together.",
    detail:
      "Configure networks, subnets and service interfaces. Define routing, NAT and firewall rules around the traffic your application needs.",
    workload: {
      label: "Application architecture / Networking",
      title: "A public application. Deliberate internal connections.",
      body: "Map the public entry point, application dependencies and administration path. Configure their allowed sources, protocols and ports, then test the resulting connections.",
      outcome:
        "An application traffic map your team can understand and maintain as services change.",
    },
    layers: [
      "Public application entry point",
      "Routes · NAT · firewall rules",
      "Application services & subnets",
    ],
    facts: [
      ["Configuration", "Networks, subnets and service interfaces"],
      ["Traffic controls", "Routes, NAT and firewall rules"],
      ["Best fit", "Multi-service application connectivity"],
    ],
    responsibilities: [
      [
        "Platform tools",
        "Network resources, route tables and supported firewall configuration workflows.",
      ],
      [
        "Your team",
        "Address planning, access policy and end-to-end application connectivity tests.",
      ],
      [
        "Environment details",
        "Available network modes and address pools determine the supported traffic paths. No upstream DDoS or private interconnect commitment is implied.",
      ],
    ],
  },
  dns: {
    action: { label: "Read the DNS setup guide", href: "/docs/domains" },
    promise: "Connect your domain to your application.",
    detail:
      "Manage DNS zones, records and supported certificate workflows alongside your services. Track changes and verify the resulting endpoint.",
    workload: {
      label: "Application architecture / DNS & certificates",
      title: "Move the endpoint. Keep the name.",
      body: "Prepare the destination service, review the record TTL and update the DNS value. Check authoritative resolution and the certificate presented by the endpoint before cutover.",
      outcome:
        "A named application endpoint with a documented DNS change and certificate installation path.",
    },
    layers: [
      "Application domain",
      "DNS record · destination · TTL",
      "Service endpoint & TLS certificate",
    ],
    facts: [
      ["Naming", "Zones and DNS records"],
      ["Service identity", "Certificate operations and bindings"],
      ["Best fit", "Application domains and endpoint changes"],
    ],
    responsibilities: [
      [
        "Platform tools",
        "Record changes, operation tracking and supported certificate workflows.",
      ],
      [
        "Your team",
        "Registrar delegation, domain access and checking the certificate at the endpoint.",
      ],
      [
        "Environment details",
        "DNSSEC and automated certificate processing require configured services. Domain registration stays with your registrar.",
      ],
    ],
  },
  "cloud-shell": {
    action: { label: "Open the console", href: "/app" },
    promise: "A command line alongside your console.",
    detail:
      "Use the Ecocloud CLI in an authenticated browser workspace. Discover commands and inspect account resources without a local CLI installation.",
    workload: {
      label: "Operational workspace / Cloud Shell",
      title: "Find the resource. Keep the context.",
      body: "Open a shell from the console, list your services and use command help to discover supported tasks. Save useful output before the session deadline.",
      outcome:
        "A browser-based workspace for a bounded operational task, close to the resources you are inspecting.",
    },
    layers: [
      "Authenticated account",
      "Browser workspace · Ecocloud CLI",
      "Supported account operations",
    ],
    facts: [
      ["Access", "Authenticated browser workspace"],
      ["Interface", "Ecocloud CLI and command help"],
      ["Best fit", "Resource inspection and guided tasks"],
    ],
    responsibilities: [
      [
        "Platform tools",
        "Workspace lifecycle, terminal access and supported CLI operations.",
      ],
      [
        "Your team",
        "Command selection, account credentials and saving work before the session ends.",
      ],
      [
        "Access boundaries",
        "Account policy and session limits apply. Guest SSH access is separate; use application runtimes for long-running services.",
      ],
    ],
  },
};
